import * as Sentry from '@sentry/react';
import { redirectSuccess } from '@/utils/flashMessage';
import { getFieldErrorsFromIdentityResult, isIdentityResult } from './auth';
import client, { formError, formSuccess } from './client';
import { formatErrorMessage } from './errors';

export type Invitation = Awaited<ReturnType<typeof getInvitations>>[number];

export async function getInvitations() {
  const { data, error } = await client.GET('/user/invitations');

  if (!data) {
    console.error('Error fetching invitations', error);
    Sentry.captureMessage(
      formatErrorMessage('Error fetching invitations', error),
      {
        level: 'error',
        extra: { error },
      },
    );
    throw new Error('Unable to fetch invitations');
  }

  return data;
}

export async function acceptInvitations() {
  const { error } = await client.POST('/user/invitations');

  if (error) {
    console.error('Error accepting invitations', error);
    Sentry.captureMessage(
      formatErrorMessage('Error accepting invitations', error),
      {
        level: 'error',
        extra: { error },
      },
    );
    throw new Error('Unable to accept invite');
  }

  return formSuccess('Invite accepted.');
}

export async function acceptInvite(
  email: string,
  firstName: string,
  lastName: string,
  password: string,
  passwordToken: string,
) {
  const { error } = await client.POST('/user/acceptinvite', {
    body: {
      email,
      firstName,
      lastName,
      password,
      passwordToken,
    },
  });

  if (error) {
    if (isIdentityResult(error)) {
      // Check for InvalidToken error
      const hasInvalidToken = error.errors.some(
        (e) => e.code === 'InvalidToken',
      );
      if (hasInvalidToken) {
        return formError(
          'Your invite has expired. Please contact your local office.',
        );
      }

      const fieldErrors = getFieldErrorsFromIdentityResult(error);

      if (Object.keys(fieldErrors).length > 0) {
        return formError(
          'Please check your details and try again.',
          fieldErrors,
        );
      }
    } else if (error.status === 404) {
      return formError(
        'Your invite has expired. Please contact your local office.',
      );
    }

    Sentry.captureMessage(formatErrorMessage('Error accepting invite', error), {
      level: 'error',
      extra: { error },
    });

    return formError('Something went wrong. Please try again.');
  }

  return redirectSuccess(
    '/login',
    'Thanks for accepting the invite! You can now log in with your new account.',
  );
}
